import type { VoidComponent } from "solid-js";
import { createEffect, createMemo, createSignal, createUniqueId, For, Index, Match, Show, Switch } from "solid-js";
import { useTranslation } from "../../state/i18n";
import InfoButton from "../infoButton/InfoButton";
import { IconButton, TextAndIconButton } from "../button/Button";
import type {
  DataSourceFieldCondition,
  FormScope,
  RuleCondition,
  RuleConditionKey,
  RuleOperator,
} from "../../state/formVersion";
import FieldId from "../../fields/FieldId";
import { CompactTextInput } from "../inputs/TextInput";
import { MAX_RULE_CONDITIONS, USER_CONDITION_KEYS } from "../../constants";
import { isEmpty, isNil, uniq } from "lodash-es";
import { fields } from "../../fields/fields";
import type { SearchFieldProperties } from "../../fields/searchItems/SearchField";
import InlineMessage from "../inlineMessage/InlineMessage";
import type { Issue } from "../../state/validation";
import { getErrorMessagesForKey } from "../../utils/validationParserUtil";
import { getPredefinedOptions } from "../../utils/ruleUtil";
import type { FieldConfig, RuleConditionVariant, VariableConfig } from "../../fields/types";
import VariableId from "../../variables/VariableId";
import toVariableDefinition from "../../variables/toVariableDefinition";
import SingleSelectWithSearch from "../menu/SingleSelectWithSearch";
import type { DynamicValueId, SelectOption, SelectOptions } from "../../types/Menu";
import { SingleSelect } from "../menu/Select";
import MetaVariableReference from "../../metaVariables/MetaVariableReference";
import type { DataSourceInfoDto } from "../../services/ApiService";

type RuleConditionsProps = {
  scope: FormScope;
  value: Partial<RuleCondition>[];
  options: SelectOptions<DynamicValueId>;
  onChange: (condition: Partial<RuleCondition>[], changedCondition: Partial<RuleCondition>) => void;
  operator: RuleOperator;
  onOperatorChange: (operator: RuleOperator) => void;
  unscopedGetField: <T = Record<string, unknown>>(fieldId: FieldId) => FieldConfig<T> | undefined; // Needs to be unscoped, since root fields are valid options
  getVariable: (variableId: VariableId) => VariableConfig | undefined;
  dataSources: DataSourceInfoDto[];
  issues: Issue[];
};

const RuleConditions: VoidComponent<RuleConditionsProps> = (props) => {
  const { t } = useTranslation();

  const keyOptions = createMemo<SelectOption<RuleConditionKey>[]>(() => [
    { variant: "text", label: t(($) => $.RULES_CONDITIONS_OPERATOR_IS), value: "is" },
    { variant: "text", label: t(($) => $.RULES_CONDITIONS_OPERATOR_STARTS_WITH), value: "startsWith" },
    { variant: "text", label: t(($) => $.RULES_CONDITIONS_OPERATOR_ENDS_WITH), value: "endsWith" },
    { variant: "text", label: t(($) => $.RULES_CONDITIONS_OPERATOR_CONTAINS), value: "contains" },
    { variant: "text", label: t(($) => $.RULES_CONDITIONS_OPERATOR_GREATER_THAN), value: "greaterThan" },
    { variant: "text", label: t(($) => $.RULES_CONDITIONS_OPERATOR_LESS_THAN), value: "lessThan" },
    { variant: "text", label: t(($) => $.RULES_CONDITIONS_OPERATOR_HAS_VALUE), value: "hasValue" },
  ]);

  const onChange = (condition: Partial<RuleCondition>, index: number) => {
    props.onChange([...props.value.slice(0, index), condition, ...props.value.slice(index + 1)], condition);
  };

  const onAdd = (condition: Partial<RuleCondition>) => {
    props.onChange([...props.value, condition], condition);
  };

  const onDelete = (index: number) => {
    const deletedCondition = props.value[index];
    props.onChange(
      props.value.filter((_, i) => i !== index),
      deletedCondition,
    );
  };

  return (
    <section aria-labelledby="conditions-title" class="flex flex-col gap-4">
      <div class="flex items-center">
        <h3 id="conditions-title" class="text-header-lg text-primary">
          {t(($) => $.RULES_CONDITIONS_TITLE)}
        </h3>
        <InfoButton
          title={t(($) => $.RULES_CONDITIONS_TITLE)}
          classList={{ "ml-auto": true }}
          description={t(($) => $.RULES_CONDITIONS_DESCRIPTION)}
          ariaLabel={t(($) => $.RULES_CONDITIONS_TITLE)}
        />
      </div>
      <div class="flex flex-col gap-2">
        <Index
          each={props.value}
          fallback={
            <EmptyCondition
              onAdd={onAdd}
              options={props.options}
              getField={props.unscopedGetField}
              index={0}
              operator={props.operator}
              onOperatorChange={props.onOperatorChange}
              scope={props.scope}
            />
          }
        >
          {(condition, index) => {
            const [value, setValue] = createSignal<FieldId | VariableId | MetaVariableReference | undefined | null>(
              null,
            );
            const [conditionConfig, setConditionConfig] = createSignal<ConditionConfig | null>(null);
            const errors = createMemo(() =>
              getErrorMessagesForKey("conditions", props.issues)?.filter((issue) => issue.index === index),
            );

            createEffect(() => {
              const unwrappedCondition = condition();
              if (
                (unwrappedCondition.type === "FIELD" || unwrappedCondition.type === "FIELD_DATA_SOURCE") &&
                !isNil(unwrappedCondition.fieldId)
              ) {
                setValue(unwrappedCondition.fieldId satisfies FieldId);
                setConditionConfig(
                  getFieldConditionConfig(
                    props.dataSources,
                    props.unscopedGetField(unwrappedCondition.fieldId),
                    unwrappedCondition.key,
                  ),
                );
              }
              if (unwrappedCondition.type === "VARIABLE" && !isNil(unwrappedCondition.variableId)) {
                setValue(unwrappedCondition.variableId satisfies VariableId);
                setConditionConfig(getVariableConditionConfig(props.getVariable(unwrappedCondition.variableId)));
              }
              if (unwrappedCondition.type === "USER") {
                setValue(MetaVariableReference.from("info.userId"));
                setConditionConfig({ variant: "text", keys: USER_CONDITION_KEYS });
              }
            });

            return (
              <div class="flex grow flex-row items-center gap-2">
                <ConditionHeader index={index} onOperatorChange={props.onOperatorChange} operator={props.operator} />
                <div class="grid w-full grid-cols-4 gap-2">
                  <SingleSelectWithSearch<DynamicValueId>
                    id={createUniqueId()}
                    ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_FIELD_ARIA)}
                    placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_FIELD_PLACEHOLDER)}
                    width="480px"
                    onSelect={(id) => {
                      const unwrappedCondition = condition();
                      if (isNil(id)) {
                        return;
                      }
                      if (id instanceof FieldId) {
                        const field = props.unscopedGetField(id);
                        onChange(
                          {
                            type: field?.type === "search" ? "FIELD_DATA_SOURCE" : "FIELD",
                            fieldId: id,
                          },
                          index,
                        );
                      } else if (
                        id instanceof VariableId &&
                        !(unwrappedCondition.type === "VARIABLE" && id.equals(unwrappedCondition.variableId))
                      ) {
                        onChange({ type: "VARIABLE", variableId: id }, index);
                      } else {
                        onChange({ type: "USER" }, index);
                      }
                    }}
                    value={value()}
                    options={props.options}
                    errorMessage={errors().find((x) => x.property === "fieldId")?.message}
                  />
                  <Show when={conditionConfig()?.columns}>
                    {(options) => (
                      <SingleSelect
                        placeholder={t(($) => $.RULES_CONDITIONS_COLUMN_PLACEHOLDER)}
                        ariaLabel={t(($) => $.RULES_CONDITIONS_COLUMN_ARIA)}
                        onSelect={(column) => {
                          const unwrappedCondition = condition();
                          if (
                            unwrappedCondition.type === "FIELD_DATA_SOURCE" &&
                            column === unwrappedCondition.fieldObjectKey
                          ) {
                            return;
                          }
                          onChange({ ...condition(), type: "FIELD_DATA_SOURCE", fieldObjectKey: column }, index);
                        }}
                        options={options()}
                        value={
                          condition().type === "FIELD_DATA_SOURCE"
                            ? (condition() as DataSourceFieldCondition).fieldObjectKey
                            : undefined
                        }
                        hasError={!isNil(errors().find((x) => x.property === "fieldObjectKey")?.message)}
                        sameWidth={false}
                      />
                    )}
                  </Show>
                  <Show when={conditionConfig()?.keys}>
                    {(options) => (
                      <SingleSelect
                        placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_PLACEHOLDER)}
                        ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_ARIA)}
                        onSelect={(key) => {
                          if (condition().key === key) {
                            return;
                          }
                          onChange({ ...condition(), key, value: undefined }, index);
                        }}
                        options={keyOptions().filter((key) => options().includes(key.value))}
                        value={condition().key}
                        hasError={!isNil(errors().find((x) => x.property === "key")?.message)}
                        sameWidth={false}
                      />
                    )}
                  </Show>
                  <Show when={!isNil(condition().key)}>
                    <Switch>
                      <Match when={condition().key === "hasValue"}>
                        <SingleSelect
                          ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_ARIA)}
                          placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_PLACEHOLDER)}
                          onSelect={(value) => {
                            onChange({ ...condition(), value }, index);
                          }}
                          options={[
                            {
                              variant: "text",
                              label: t(($) => $.RULES_CONDITIONS_OPERATOR_HAS_VALUE_YES),
                              value: true,
                            },
                            {
                              variant: "text",
                              label: t(($) => $.RULES_CONDITIONS_OPERATOR_HAS_VALUE_NO),
                              value: false,
                            },
                          ]}
                          value={condition().value}
                          hasError={!isNil(errors().find((x) => x.property === "value")?.message)}
                          sameWidth={false}
                        />
                      </Match>
                      <Match when={conditionConfig()?.variant === "select"}>
                        <SingleSelect
                          ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_ARIA)}
                          placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_PLACEHOLDER)}
                          onSelect={(value) => {
                            onChange({ ...condition(), value }, index);
                          }}
                          options={conditionConfig()?.options ?? []}
                          value={condition().value}
                          hasError={!isNil(errors().find((x) => x.property === "value")?.message)}
                          sameWidth={false}
                        />
                      </Match>
                      <Match when={conditionConfig()?.variant === "boolean"}>
                        <SingleSelect
                          ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_ARIA)}
                          placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_PLACEHOLDER)}
                          onSelect={(value) => {
                            onChange({ ...condition(), value }, index);
                          }}
                          options={[
                            { variant: "text", label: t(($) => $.RULES_CONDITIONS_CHECKED), value: true },
                            { variant: "text", label: t(($) => $.RULES_CONDITIONS_UNCHECKED), value: false },
                          ]}
                          value={condition().value}
                          hasError={!isNil(errors().find((x) => x.property === "value")?.message)}
                          sameWidth={false}
                        />
                      </Match>
                      <Match
                        when={conditionConfig()?.variant === "text" || conditionConfig()?.variant === "dataSource"}
                      >
                        <CompactTextInput
                          id={`condition-${index}`}
                          ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_ARIA)}
                          value={(condition().value as string) ?? ""}
                          placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_VALUE_PLACEHOLDER)}
                          onBlur={(e) => {
                            onChange({ ...condition(), value: e.target.value.trim() }, index);
                          }}
                          errorMessage={errors().find((x) => x.property === "value")?.message}
                        />
                      </Match>
                    </Switch>
                  </Show>
                </div>
                <div class="ml-auto shrink-0">
                  <IconButton
                    variant="tertiary"
                    icon="trash"
                    size="medium"
                    aria-label={t(($) => $.DELETE_ARIA)}
                    onClick={() => {
                      onDelete(index);
                    }}
                  />
                </div>
              </div>
            );
          }}
        </Index>
      </div>
      <div class="flex flex-col gap-4">
        <Show
          when={getErrorMessagesForKey(
            "conditions",
            props.issues.filter((issue) => issue.scope === "parent"),
          )}
        >
          {(issues) => (
            <Switch>
              <Match when={issues().length === 1}>
                <InlineMessage variant="danger" label={issues()[0].message} />
              </Match>
              <Match when={issues().length > 1}>
                <InlineMessage variant="danger">
                  <span>{t(($) => $.VALIDATION_RULES_CONDITION_INLINE_MESSAGE_DESCRIPTION)}</span>
                  <ul class="list-none">
                    <For each={issues()}>
                      {(issue) => (
                        <li class="flex">
                          <span class="mr-1.5 ml-2">•</span>
                          {issue.message}
                        </li>
                      )}
                    </For>
                  </ul>
                </InlineMessage>
              </Match>
            </Switch>
          )}
        </Show>
        <Switch>
          <Match when={props.value?.length <= MAX_RULE_CONDITIONS}>
            <div>
              <TextAndIconButton
                disabled={isEmpty(props.value)}
                text={t(($) => $.RULES_CONDITIONS_ADD_BUTTON)}
                variant="light"
                size="medium"
                icon="plus"
                onClick={() => {
                  onAdd({ type: "EMPTY" });
                }}
              />
            </div>
          </Match>
          <Match when={props.value?.length > MAX_RULE_CONDITIONS}>
            <InlineMessage
              variant="warning"
              label={t(($) => $.RULES_CONDITIONS_LIMIT_INLINE, { count: MAX_RULE_CONDITIONS })}
            />
          </Match>
        </Switch>
      </div>
    </section>
  );
};

type EmptyConditionProps = {
  scope: FormScope;
  options: SelectOptions<DynamicValueId>;
  onAdd: (value: Partial<RuleCondition>) => void;
  operator: RuleOperator;
  onOperatorChange: (operator: RuleOperator) => void;
  getField: <T = Record<string, unknown>>(fieldId: FieldId) => FieldConfig<T> | undefined;
  index: number;
};
const EmptyCondition: VoidComponent<EmptyConditionProps> = (props) => {
  const { t } = useTranslation();
  return (
    <div class="flex grow flex-row items-center gap-2">
      <ConditionHeader index={props.index} onOperatorChange={props.onOperatorChange} operator={props.operator} />
      <div class="grid w-full grid-cols-4 gap-2">
        <SingleSelectWithSearch
          id={createUniqueId()}
          ariaLabel={t(($) => $.RULES_CONDITIONS_CONDITION_FIELD_ARIA)}
          placeholder={t(($) => $.RULES_CONDITIONS_CONDITION_FIELD_PLACEHOLDER)}
          onSelect={(value) => {
            if (value instanceof FieldId) {
              const field = props.getField(value);
              props.onAdd({ type: field?.type === "search" ? "FIELD_DATA_SOURCE" : "FIELD", fieldId: field?.uid });
            } else if (value instanceof VariableId) {
              props.onAdd({ type: "VARIABLE", variableId: value });
            } else {
              props.onAdd({ type: "USER" });
            }
          }}
          options={props.options}
          errorMessage={t(($) => $.VALIDATION_RULES_CONDITIONS_REQUIRED)}
          width="480px"
        />
      </div>
    </div>
  );
};

type ConditionHeaderProps = {
  index: number;
  operator: RuleOperator;
  onOperatorChange: (operator: RuleOperator) => void;
};
const ConditionHeader: VoidComponent<ConditionHeaderProps> = (props) => {
  const { t } = useTranslation();
  return (
    <Switch
      fallback={
        <span class="w-18 shrink-0 p-2 text-base-medium text-secondary">
          {props.operator === "AND" ? t(($) => $.RULES_CONDITIONS_AND) : t(($) => $.RULES_CONDITIONS_OR)}
        </span>
      }
    >
      <Match when={props.index === 0}>
        <h4 class="w-18 shrink-0 pl-2 text-header-md text-primary">{t(($) => $.RULES_CONDITIONS_IF)}</h4>
      </Match>
      <Match when={props.index === 1}>
        <div class="w-18 shrink-0">
          <SingleSelect<RuleOperator>
            ariaLabel={t(($) => $.RULES_CONDITIONS_OPERATOR_ARIA)}
            onSelect={(value) => props.onOperatorChange(value)}
            value={props.operator}
            options={[
              {
                variant: "text",
                label: t(($) => $.RULES_CONDITIONS_AND),
                value: "AND",
                description: t(($) => $.RULES_CONDITIONS_AND_DESCRIPTION),
              },
              {
                variant: "text",
                label: t(($) => $.RULES_CONDITIONS_OR),
                value: "OR",
                description: t(($) => $.RULES_CONDITIONS_OR_DESCRIPTION),
              },
            ]}
            sameWidth={false}
            hasError={false}
          />
        </div>
      </Match>
    </Switch>
  );
};

type ConditionConfig = {
  variant: RuleConditionVariant;
  keys: RuleConditionKey[];
  columns?: SelectOption<string>[];
  options?: SelectOption<string>[];
};

const getColumns = (field: FieldConfig<unknown>, dataSources: DataSourceInfoDto[]): SelectOption<string>[] => {
  if (field.type !== "search") {
    return [];
  }
  const searchField = field as FieldConfig<SearchFieldProperties>;
  if (!isNil(searchField?.properties.data_source_configuration)) {
    const dataSource = dataSources.find(
      (dataSource) => dataSource.id === searchField?.properties.data_source_configuration.id,
    );
    return (
      dataSource?.columnMapping.map((column) => ({ variant: "text", label: column.label, value: column.id })) ?? []
    );
  }

  return [];
};

const getFieldConditionConfig = (
  dataSources: DataSourceInfoDto[],
  field?: FieldConfig,
  key?: RuleConditionKey,
): ConditionConfig => {
  if (isNil(field)) {
    return { variant: "none", keys: [] };
  }
  const condition = fields[field.type].ruleBehaviour?.conditions;
  return {
    variant: condition?.variant ?? "none",
    keys: uniq([...(condition?.keys ?? []), ...(key ? [key] : [])]), // We removed support for some keys, but keep them if they are set
    columns: condition?.variant === "dataSource" ? getColumns(field, dataSources) : undefined,
    options: condition?.variant === "select" ? getPredefinedOptions(field) : undefined,
  };
};

const getVariableConditionConfig = (variable?: VariableConfig): ConditionConfig => {
  if (isNil(variable)) {
    return { variant: "none", keys: [] };
  }
  const conditionBehaviour = toVariableDefinition(variable.type).ruleConditionBehaviour;
  return {
    variant: conditionBehaviour.variant,
    keys: conditionBehaviour.keys,
    columns: undefined,
    options: undefined,
  };
};

export default RuleConditions;
